<script setup>
/**
 * RatingCrystals — inline 1-5 crystal-ball rating revealed beneath the
 * post-reading buttons (mirrors the Share reveal: no chat turn). The seeker
 * rates their OWN reading; re-tapping changes it (the endpoint upserts). On
 * submit it confirms with the reading's new average + count.
 */
import { computed, ref, watch } from 'vue'
import { store } from '@/store'
import { rateReading } from '@/api-service'

// readingId defaults to the seeker's own current reading (post-reading rate);
// pass a shared reading's short_uuid to rate someone else's (e.g. coven resonance).
const props = defineProps({
  readingId: { type: String, default: '' },
  // 'gift': the package rated in its room (reading_rated carries via, R4.31).
  via: { type: String, default: '' },
})
const emit = defineEmits(['rated'])

// The reading this rates, and her rating of it already saved (store.reading_ratings,
// filled from load-reading-session's my_rating and from every save): a remount (the
// box comes back after Talk it through closes, or the reading reopened) shows it.
const ratingKey = computed(() => props.readingId || store.latest_reading_session || '')
const saved = computed(() => Number(((store.reading_ratings || {})[ratingKey.value]) || 0))
const hovered = ref(0)
const selected = ref(saved.value)
watch(saved, (n) => { if (!saving.value && n) selected.value = n })
const saving = ref(false)
const confirmed = ref(false)
const summary = ref(null) // { average_rating, rating_count }
const validationMsg = ref('') // e.g. self-rating blocked, surfaced on click

// "Resonance" scale — how the reading landed for the seeker (1→5).
const LABELS = ['', 'Distant', 'Faint', 'Felt it', 'Resonant', 'Profound']

async function choose(n) {
  if (saving.value) return
  selected.value = n
  saving.value = true
  confirmed.value = false
  validationMsg.value = ''
  try {
    const key = ratingKey.value
    const data = await rateReading(key, n, store.session_id,
      props.via ? { via: props.via } : undefined)
    if (key) store.reading_ratings = { ...(store.reading_ratings || {}), [key]: n }
    summary.value = data
    confirmed.value = true
    emit('rated', data)
  } catch (e) {
    // The server validates on click (e.g. a Mystic can't rate their own
    // reading) — surface its explanation instead of recording the rating.
    const msg = e && e.response && e.response.data && e.response.data.error
    if (msg) {
      validationMsg.value = msg
      selected.value = 0 // nothing was recorded; clear the highlight
    } else {
      console.error('Error rating reading:', e)
    }
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <div class="rc">
    <div class="rc-row">
      <span class="rc-prompt"><slot name="prompt">How does this resonate?</slot></span>
      <div class="rc-balls" role="radiogroup" aria-label="Rate this reading 1 to 5 crystal balls">
        <button
          v-for="n in 5" :key="n" type="button" class="rc-ball"
          :class="{ filled: n <= (hovered || selected) }"
          :aria-label="`${n} crystal ball${n > 1 ? 's' : ''}`"
          :aria-pressed="n === selected"
          @mouseenter="hovered = n" @mouseleave="hovered = 0"
          @focus="hovered = n" @blur="hovered = 0"
          @click="choose(n)"
        >🔮</button>
      </div>
      <span class="rc-label">{{ LABELS[hovered || selected] }}</span>
    </div>
    <div v-if="validationMsg" class="rc-validation">⚠ {{ validationMsg }}</div>
    <transition name="rc-fade">
      <div v-if="confirmed && summary && !validationMsg" class="rc-confirm">
        ✦ Thank you — this reading now averages
        {{ summary.average_rating }}🔮 ({{ summary.rating_count }})
      </div>
    </transition>
  </div>
</template>

<style scoped>
.rc {
  margin: 12px auto 0; padding: 10px 14px; max-width: 100%;
  background: #faf7ff; border: 1px solid #ece3f7; border-radius: 14px;
  animation: rc-in 0.2s ease;
}
@keyframes rc-in { from { opacity: 0; transform: translateY(-4px); } }
.rc-row { display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 10px; }
.rc-prompt { font-family: 'Cinzel', serif; font-size: 13px; font-weight: 600; color: #4a236b; }
.rc-balls { display: inline-flex; gap: 4px; }
.rc-ball {
  border: none; background: none; cursor: pointer; padding: 2px;
  font-size: 22px; line-height: 1; filter: grayscale(1) opacity(0.4);
  transition: transform 0.12s ease, filter 0.12s ease;
}
.rc-ball.filled { filter: none; }
.rc-ball:hover { transform: translateY(-2px) scale(1.08); }
.rc-label { min-width: 92px; font-size: 12.5px; font-style: italic; color: #6b5a82; }
.rc-confirm { margin-top: 8px; text-align: center; font-size: 12.5px; color: #2f8f2f; font-weight: 600; }
.rc-validation { margin-top: 8px; text-align: center; font-size: 12.5px; color: #b4622a; font-weight: 600; }
.rc-fade-enter-active { transition: opacity 0.3s ease; }
.rc-fade-enter-from { opacity: 0; }
</style>
